import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PhoneLink from '../../components/PhoneLink';
import { useAuth } from '../../context/AuthContext';
import { useWidgetVisibility } from '../../context/WidgetVisibilityContext';
import { floorService, invoiceService, roomService, tenantService } from '../../services/api';
import { Floor, Invoice, Room, User } from '../../types';
import { monthBounds } from '../../utils/dashboardFinance';
import {
  matchesTenantPaymentFilter,
  PAYMENT_MONTH_NAMES,
  TenantPaymentFilter,
} from '../../utils/tenantPaymentStatus';
import UserPhotoCell from '../../components/UserPhotoCell';
import { filterResidentTenants } from '../../utils/residentTenants';
import {
  buildFloorRoomLookup,
  buildTenantFinancialRow,
  computeMonthlyExpectedCollection,
  computePendingPaymentsSummary,
  formatINR,
  groupInvoicesByUserId,
  isPaidInMonth,
  isUnpaidInMonth,
  tenantDisplayName,
  tenantLocation,
  TenantFinancialRow,
} from './reportsUtils';

type FinancialFilter =
  | 'all'
  | 'current_paid'
  | 'current_unpaid'
  | 'due'
  | 'prev_unpaid'
  | 'paid_in_month'
  | 'unpaid_in_month'
  | 'has_dues';

const FILTER_OPTIONS: { value: FinancialFilter; label: string }[] = [
  { value: 'all', label: 'All tenants' },
  { value: 'current_paid', label: 'Paid this month' },
  { value: 'current_unpaid', label: 'Unpaid this month' },
  { value: 'paid_in_month', label: 'Paid in selected month' },
  { value: 'unpaid_in_month', label: 'Unpaid in selected month' },
  { value: 'due', label: 'Due (balance this month)' },
  { value: 'prev_unpaid', label: 'Previous months unpaid' },
  { value: 'has_dues', label: 'Any outstanding dues' },
];

const ReportsFinancials: React.FC = () => {
  const { hostel } = useAuth();
  const { isVisible } = useWidgetVisibility();
  const now = new Date();
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<User[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FinancialFilter>('all');
  const [filterMonth, setFilterMonth] = useState(now.getMonth());
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [statusTab, setStatusTab] = useState<'ACTIVE' | 'VACATING' | 'ALL'>('ACTIVE');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const period = useMemo(() => monthBounds(filterYear, filterMonth + 1), [filterYear, filterMonth]);
  const locationLookup = useMemo(() => buildFloorRoomLookup(floors, rooms), [floors, rooms]);

  useEffect(() => {
    if (!hostel?.id) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        setLoading(true);
        const status = statusTab === 'ALL' ? 'ALL_USERS' : statusTab;
        const [t, fl, rm, inv] = await Promise.all([
          tenantService.getTenantsByHostel(hostel.id, status),
          floorService.getFloorsByHostel(hostel.id),
          roomService.getRoomsByHostel(hostel.id),
          invoiceService.getAllInvoices(),
        ]);
        setTenants(filterResidentTenants(t).filter((x) => !x.isDeleted));
        setFloors(fl.filter((x) => x.isActive !== false));
        setRooms(rm.filter((x) => x.isActive !== false));
        setInvoices(inv);
      } catch {
        setTenants([]);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [hostel?.id, statusTab]);

  const hostelInvoices = useMemo(
    () => invoices.filter((i) => !i.isDeleted && Number(i.hostelId) === hostel?.id),
    [invoices, hostel?.id],
  );
  const invoicesByUser = useMemo(() => groupInvoicesByUserId(hostelInvoices), [hostelInvoices]);

  const rows: TenantFinancialRow[] = useMemo(
    () =>
      tenants.map((t) => {
        const invs = invoicesByUser.get(t.id) || [];
        return buildTenantFinancialRow(t, invs, period.start, period.end);
      }),
    [tenants, invoicesByUser, period],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const monthName = PAYMENT_MONTH_NAMES[filterMonth];
    const yearStr = String(filterYear);

    return rows.filter((row) => {
      const { tenant } = row;
      const invs = invoicesByUser.get(tenant.id) || [];

      if (q) {
        const name = tenantDisplayName(tenant).toLowerCase();
        const phone = (tenant.phoneNumber || '').toLowerCase();
        const loc = tenantLocation(tenant, locationLookup).toLowerCase();
        if (!name.includes(q) && !phone.includes(q) && !loc.includes(q)) return false;
      }

      if (filter === 'has_dues') return row.remainingAmount > 0.01;
      if (filter === 'paid_in_month') return isPaidInMonth(tenant, invs, monthName, yearStr);
      if (filter === 'unpaid_in_month') return isUnpaidInMonth(tenant, invs, monthName, yearStr);
      if (filter === 'all') return true;

      const paymentFilter = filter as TenantPaymentFilter;
      if (['current_paid', 'current_unpaid', 'due', 'prev_unpaid'].includes(filter)) {
        return matchesTenantPaymentFilter(tenant, paymentFilter, invoicesByUser);
      }
      return true;
    });
  }, [rows, query, filter, filterMonth, filterYear, invoicesByUser, locationLookup]);

  const monthlyExpected = useMemo(() => computeMonthlyExpectedCollection(tenants), [tenants]);

  const pendingPaymentsActive = useMemo(
    () => computePendingPaymentsSummary(tenants, invoicesByUser, 9999).totalAmount,
    [tenants, invoicesByUser],
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => ({
          outstanding: acc.outstanding + r.expectedCollection,
          collected: acc.collected + r.collectedInPeriod,
          remaining: acc.remaining + r.remainingAmount,
          advance: acc.advance + r.advancePaid,
        }),
        { outstanding: 0, collected: 0, remaining: 0, advance: 0 },
      ),
    [filtered],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [filter, query, statusTab, filterMonth, filterYear]);

  const hasActiveFilters =
    query.trim() !== '' ||
    filter !== 'all' ||
    filterMonth !== now.getMonth() ||
    filterYear !== now.getFullYear() ||
    statusTab !== 'ACTIVE';

  const clearFilters = () => {
    const n = new Date();
    setQuery('');
    setFilter('all');
    setFilterMonth(n.getMonth());
    setFilterYear(n.getFullYear());
    setStatusTab('ACTIVE');
  };

  if (loading) return <div className="reports-loading">Loading financials…</div>;
  if (!hostel?.id) return <div className="reports-empty">Select a hostel to view financial reports.</div>;

  return (
    <div className="reports-section">
      <div className="reports-section__toolbar">
        <div>
          <h2 className="reports-section__heading">Financials</h2>
          <p className="reports-section__sub">Tenant collections, dues, advances, and payment status</p>
        </div>
      </div>

      {isVisible('reports.financials.summary') && (
      <div className="reports-summary-strip">
        <div>
          <span>Expected collection</span>
          <strong>{formatINR(monthlyExpected.amount)}</strong>
          <small className="reports-summary-strip__hint">
            {monthlyExpected.tenantsWithRent} active tenants with rent
          </small>
        </div>
        <div>
          <span>Collected (period)</span>
          <strong className="reports-text--ok">{formatINR(totals.collected)}</strong>
        </div>
        <div>
          <span>Pending payments</span>
          <strong className="reports-text--warn">{formatINR(pendingPaymentsActive)}</strong>
          <small className="reports-summary-strip__hint">Active tenants only</small>
        </div>
        <div>
          <span>Total advance paid</span>
          <strong>{formatINR(totals.advance)}</strong>
        </div>
      </div>
      )}

      {isVisible('reports.financials.table') && (
      <>
      <div className="reports-filter-bar">
        <input
          type="search"
          className="reports-search"
          placeholder="Search name, phone, room…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select className="reports-select" value={filter} onChange={(e) => setFilter(e.target.value as FinancialFilter)}>
          {FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select className="reports-select" value={filterMonth} onChange={(e) => setFilterMonth(Number(e.target.value))}>
          {PAYMENT_MONTH_NAMES.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <select className="reports-select" value={filterYear} onChange={(e) => setFilterYear(Number(e.target.value))}>
          {[filterYear - 1, filterYear, filterYear + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <div className="reports-tabs">
          {(['ACTIVE', 'VACATING', 'ALL'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`reports-tab${statusTab === tab ? ' reports-tab--active' : ''}`}
              onClick={() => setStatusTab(tab)}
            >
              {tab === 'ALL' ? 'All' : tab === 'VACATING' ? 'Vacating' : 'Active'}
            </button>
          ))}
        </div>
        {hasActiveFilters ? (
          <button type="button" className="reports-clear-btn" onClick={clearFilters}>
            Clear filters
          </button>
        ) : null}
      </div>

      <div className="reports-table-wrap responsive-table-wrap">
        <table className="reports-table responsive-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Location</th>
              <th>Rent</th>
              <th>This month</th>
              <th>Prev. unpaid</th>
              <th>Expected</th>
              <th>Collected (period)</th>
              <th>Remaining</th>
              <th>Advance paid</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr className="responsive-table__empty">
                <td colSpan={10} className="reports-table__empty" data-label="">
                  No tenants match the selected filters.
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={row.tenant.id}>
                  <td className="responsive-table__primary" data-label="Tenant">
                    <div className="reports-tenant-cell">
                      <UserPhotoCell
                        name={tenantDisplayName(row.tenant)}
                        photoUrl={row.tenant.photo}
                        size="md"
                      />
                      <div>
                        <strong>{tenantDisplayName(row.tenant)}</strong>
                        <span>
                          <PhoneLink phone={row.tenant.phoneNumber} />
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="responsive-table__full" data-label="Location">{tenantLocation(row.tenant, locationLookup)}</td>
                  <td data-label="Rent">{row.rent > 0 ? formatINR(row.rent) : '—'}</td>
                  <td data-label="This month">
                    {row.rent <= 0 ? (
                      '—'
                    ) : row.currentMonthPaid ? (
                      <span className="reports-badge reports-badge--ok">Paid</span>
                    ) : (
                      <span className="reports-badge reports-badge--warn">
                        {formatINR(row.currentMonthDue)}
                      </span>
                    )}
                  </td>
                  <td className={row.previousUnpaid > 0 ? 'reports-text--warn' : ''} data-label="Prev. unpaid">
                    {row.previousUnpaid > 0 ? formatINR(row.previousUnpaid) : '—'}
                  </td>
                  <td data-label="Expected">{formatINR(row.expectedCollection)}</td>
                  <td className="reports-text--ok" data-label="Collected">{formatINR(row.collectedInPeriod)}</td>
                  <td className={row.remainingAmount > 0 ? 'reports-text--warn' : ''} data-label="Remaining">
                    {formatINR(row.remainingAmount)}
                  </td>
                  <td data-label="Advance paid">{formatINR(row.advancePaid)}</td>
                  <td className="responsive-table__actions" data-label="">
                    <Link to={`/tenants/view/${row.tenant.id}`} className="reports-link">
                      View
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > pageSize && (
        <div className="reports-pagination">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {page} of {totalPages} ({filtered.length} tenants)
          </span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ReportsFinancials;
